import {
  useQuery,
  UseQueryOptions,
  UseQueryResult,
  QueryKey,
  useQueryClient,
} from "@tanstack/react-query";
import { useAuthentication } from "./useAuthentication";
import { useApiHelperAppContext } from "../appContexts/ApiHelperAppContext";
import axiosInstance, {
  isAxiosError,
  setAxiosToken,
} from "../helpers/axiosHelper";
import { keysToCamelCase } from "../utilities/apiUtility";
import { getErrorToast } from "../utilities/toastUtility";
import { useState } from "react";
import { useCache } from "./useCache";
import { useNavigate } from "react-router-dom";
import { routePaths } from "../routes/routePaths";

interface IApiResponse<T = any> {
  data: T;
  status: number;
  message?: string;
}

interface UseApiQueryParams<T> {
  queryKey: QueryKey;
  retry?: boolean;
  endpoint: string;
  enabled?: boolean;
  successMessage?: string;
  errorMessage?: string;
  disableSuccessToast?: boolean;
  disableErrorToast?: boolean;
  globalIsLoading?: boolean;
}

/**
 * We omit 'queryKey' and 'queryFn' from UseQueryOptions,
 * since we provide them ourselves. The rest is passed through.
 */
type UseApiQueryOptions<T> = Omit<
  UseQueryOptions<T, Error, T, QueryKey>,
  "queryKey" | "queryFn"
>;

/**
 * A custom hook for GET requests using React Query v4+,
 * with built-in token setting, loading state, error handling, and toasts.
 */
export function useApiQuery<T = unknown>(
  {
    queryKey,
    endpoint,
    enabled = true,
    globalIsLoading = true,
    disableErrorToast = true,
    retry = true,
  }: UseApiQueryParams<T>,
  options?: UseApiQueryOptions<T>
): UseQueryResult<T, Error> {
  const { currentUser } = useAuthentication();
  const { setIsLoading } = useApiHelperAppContext();
  const [, forceRender] = useState(false);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useQuery<T, Error, T, QueryKey>({
    queryKey,
    retry: retry,
    enabled,
    queryFn: async () => {
      setAxiosToken(currentUser?.authToken);

      forceRender((x) => !x);

      // Only show global loading on initial load (when there's no cached data)
      const existingData = queryClient.getQueryData(queryKey);
      const shouldShowGlobalLoader = globalIsLoading && !existingData;

      if (shouldShowGlobalLoader) {
        setIsLoading(true);
      }

      try {
        const res = await axiosInstance.get<IApiResponse<T>>(endpoint);
        const apiResponse = keysToCamelCase(res.data);

        if (res.status >= 200 && res.status < 300) {
          return apiResponse as T;
        } else {
          if (!disableErrorToast) {
            getErrorToast(apiResponse.message);
          }
          throw new Error(apiResponse?.message || "Server error.");
        }
      } catch (error) {
        if (isAxiosError(error)) {
          const status = error.response?.status;

          if (status === 401 || status === 403) {
            navigate(routePaths.logout);
          } else {
            if (!disableErrorToast) {
              const apiResponse = keysToCamelCase(
                error.response?.data as IApiResponse
              );
              const errorMessage =
                apiResponse?.message ||
                "Възникна грешка. Моля обновете страницата.";
              getErrorToast(errorMessage);
            }
          }
        }

        throw error;
      } finally {
        if (shouldShowGlobalLoader) {
          setIsLoading(false);
        }
        forceRender((x) => !x);
      }
    },
    /**
     * Merge with user-provided options, but we also define onSuccess/onError
     * so that we can show toasts and call handleRequestError.
     */
    ...options,
    // onSuccess: (data) => {
    //   // If the user gave an onSuccess in "options", call it after our logic
    //   if (!disableSuccessToast && successMessage) {
    //     getSuccessToast(successMessage);
    //   }
    //   options?.onSuccess?.(data);
    // },
    // onError: (error) => {
    //   if (!disableErrorToast) {
    //     getErrorToast(errorMessage || error.message);
    //   }
    //   handleRequestError(error, navigate, logout);
    //   options?.onError?.(error);
    // },
  });
}

export const useClearAllQueryCache = () => {
  const queryClient = useQueryClient();
  const { dispatchCacheClear } = useCache();

  const clearCache = () => {
    queryClient.clear();
    dispatchCacheClear();
  };

  return { clearCache };
};
